import { Navigate, useLocation } from 'react-router-dom';
import PageLoader from 'components/PageLoader';
import { appUrls } from 'router';
import { useAuthUserStatus } from 'hooks/useAuthUserStatus';
import { useLocaleContext } from 'hooks/useLocaleContext';

export const RETURN_TO_KEY = 'eu4ua.returnTo';

/** Path saved by the guard before sending a signed-out visitor to the home page, if any. */
export function takeReturnTo(): string | null {
  try {
    const path = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    return path && path.startsWith('/') ? path : null;
  } catch {
    return null;
  }
}

interface PrivateRouteGuardProps {
  children: JSX.Element;
  /** Let signed-in users through even before they verify their email (complete-profile). */
  allowUnverified?: boolean;
}

function PrivateRouteGuard({
  children, allowUnverified = false,
}: PrivateRouteGuardProps): JSX.Element {
  const location = useLocation();
  const { locale } = useLocaleContext();
  const { isAuthenticated, isEmailVerified, isLoading } = useAuthUserStatus();

  if (isLoading) {
    return (
      <PageLoader />
    );
  }

  if (isAuthenticated && (isEmailVerified || allowUnverified)) {
    return (
      <>{children}</>
    );
  }

  if (isAuthenticated && !isEmailVerified) {
    return (
      <Navigate replace to={appUrls.getLoginPageUrl(locale)} state={{ from: location }} />
    );
  }

  // A link from an e-mail (a conversation) opened while signed out: come back to it after login.
  if (/^\/[a-z]{2}\/messages(\/|$)/.test(location.pathname)) {
    try {
      window.sessionStorage.setItem(RETURN_TO_KEY, location.pathname);
    } catch {
      // storage unavailable: the user lands on the dashboard as before
    }
  }

  return (
    <Navigate replace to={appUrls.getHomePageUrl(locale)} state={{ from: location }} />
  );
}

export default PrivateRouteGuard;
